import { useState, useEffect } from "react";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";

/**
 * useClientSubscription - React hook for real-time subscription to a client's Firestore document.
 * Also subscribes to users/{clientId} so email fields (email, coachContactEmail, authLoginEmail)
 * always merge in, even when the org-wide users list (Recoil) is still null or missing this uid.
 *
 * @param {string} clientId - The UID of the client to subscribe to.
 * @param {string} [organizationId] - Optional organizationId. If not provided, will use Recoil selector.
 * @returns {{ client: object|null, loading: boolean, error: any }}
 */
const useClientSubscription = (clientId, organizationId) => {
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!clientId || !organizationId) {
      setClient(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    let clientPayload = null;
    let userPayload = null;
    let clientSnapReceived = false;

    const publish = () => {
      if (!clientSnapReceived) {
        return;
      }
      if (!clientPayload) {
        setClient(null);
        setLoading(false);
        return;
      }
      const merged = userPayload
        ? { ...clientPayload, ...userPayload }
        : clientPayload;
      setClient(merged);
      setLoading(false);
    };

    const clientRef = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("clients")
      .doc(clientId);

    const unsubClient = clientRef.onSnapshot(
      (doc) => {
        clientSnapReceived = true;
        clientPayload = doc.exists
          ? {
              ...doc.data(),
              id: doc.id,
              uid: doc.data()?.uid ?? doc.id,
            }
          : null;
        publish();
      },
      (err) => {
        setError(err);
        setLoading(false);
      },
    );

    const userRef = firebase.firestore().collection("users").doc(clientId);

    const unsubUser = userRef.onSnapshot(
      (doc) => {
        userPayload = doc.exists ? doc.data() : null;
        publish();
      },
      (err) => {
        console.warn('useClientSubscription: user snapshot error', err);
        userPayload = null;
        publish();
      },
    );

    return () => {
      unsubClient();
      unsubUser();
    };
  }, [clientId, organizationId]);

  return { client, loading, error };
};

export default useClientSubscription;
